import React, { useState, useMemo } from "react";
import { useFormatter } from "../../../util/formatter";
import LoaderGraph from "../../general/LoaderGraph";
import fonts_config from "./fonts_config.json";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import pdfMake from "pdfmake/build/pdfmake.js";
import { Button, Row } from "react-bootstrap";
import { useSolarCalc } from "../../../util/calculations";
import GYLogo from "../../../images/BASE64/GYLogo";
import usePeakLabels from "../../../util/peakLabels";
import moment from "moment-timezone";
import GYAxios from "../../../util/GYAxios";
import rfdc from "rfdc";

const clone = rfdc();

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs;
pdfMake.fonts = fonts_config;

const [formatNumber] = useFormatter();

// convert centimeters to points (PDFMake use Point as a unit)
const cm_to_pt = (cm) => cm * 28.346;

// IMPORTANT: DO NOT CHANGE BELOW DOC DEFINITION
// AS IT IS FOR SHOWING THE OLD INVOICES
// invoice template before having envelope with window
const old_invoice_template = (docDefData) => {
	const {
		logo,
		entitySection,
		billingName,
		billingAddress,
		data,
		label,
		copyTypes,
		copyType,
		locationSection,
		_period,
		subtotalPrice,
		vatDisplay,
		VATPrice,
		grandTotal,
		paymentSection,
	} = docDefData;
	const docDefinition = {
		pageSize: "A4",
		pageMargins: 0,
		content: [
			{
				nodeName: "DIV",
				margin: [40, 40],
				style: "invoiceMSAStyle",
				stack: [
					{
						nodeName: "DIV",
						columns: [
							{
								nodeName: "DIV",
								width: "50%",
								stack: [
									{
										image: logo,
										width: 150,
									},
									...entitySection,
									{
										text: "TO",
										style: ["boldHeader"],
									},
									{
										nodeName: "DIV",
										margin: [20, 10],
										stack: [
											{
												text: billingName,
												style: ["boldHeader"],
											},
											{
												text: billingAddress,
											},
											{
												text: `TAX ID : ${data.taxID}`,
											},
										],
									},
								],
							},
							{
								nodeName: "DIV",
								width: "50%",
								stack: [
									// second column consists of paragraphs
									{ text: label, style: "header_lg", margin: 0 },
									{
										text: copyTypes[copyType],
										style: "sub_header_2",
										margin: 0,
									},
									{
										margin: [35, 0, 0, 0],
										table: {
											body: [
												[
													{
														text: "INVOICE NO.",
														style: ["boldHeader"],
													},
													{
														text: data.invNo,
														style: ["invoice_details"],
													},
												],
												[
													{
														text: "DATE",
														style: ["boldHeader"],
													},
													{ text: data.invIssuedDate, style: ["invoice_details"] },
												],
												[
													{
														text: "Credit Term",
														style: ["boldHeader"],
													},
													{
														text: data.creditTerm + " days",
														style: ["invoice_details"],
													},
												],
												[
													{
														text: "Due date",
														style: ["boldHeader"],
													},
													{ text: data.dueDate, style: ["invoice_details"] },
												],
												locationSection[0],
												...(locationSection[1] ? [locationSection[1]] : []),
												...(data.invTopRightNote != null &&
												Object.keys(data.invTopRightNote).length > 0
													? Object.entries(data.invTopRightNote).map(
															([key, value]) => [
																{
																	text: key + ":",
																	style: ["boldHeader"],
																},
																{
																	text: value,
																	style: ["invoice_details"],
																},
															],
														)
													: []),
											],
										},
										layout: {
											defaultBorder: false,
										},
									},
								],
							},
						],
					},
					{
						style: "table_main",
						table: {
							body: [
								[
									{
										text: "DESCRIPTION",
										border: [false, true, false, true],
										style: ["table_header", "boldHeader"],
									},
									{
										border: [false, true, false, true],
										text: "QUANTITY (days)",
										alignment: "right",
										style: ["table_header", "boldHeader"],
									},
									{
										border: [false, true, false, true],
										text: "UNIT COST",
										alignment: "right",
										style: ["table_header", "boldHeader"],
									},
									{
										border: [false, true, false, true],
										text: "TOTAL",
										alignment: "right",
										style: ["table_header", "boldHeader"],
									},
								],
								[
									{
										text: `Period: ${_period}`,
										style: ["table_main_rows", "boldHeader"],
										colSpan: 4,
									},
								],
								[
									{
										text: `Note: \n${data.msa_description}`,
										style: ["table_main_rows", "msa_description"],
										alignment: "left",
										colSpan: 2,
									},
									"",
									"",
									"",
								],
								[
									{
										text: "Monitoring Service",
										style: "table_main_rows",
										rowSpan: 2,
									},
									{
										text: data.msa_day > 0 ? data.msa_day : "",
										alignment: "right",
										rowSpan: 2,
										style: "table_main_rows",
										padding: 0,
									},
									{
										text:
											data.msa_price > 0 ? formatNumber(data.msa_price, 2) : "",
										alignment: "right",
										rowSpan: 2,
										style: "table_main_rows",
									},
									{
										text: formatNumber(subtotalPrice, 2),
										alignment: "right",
										rowSpan: 2,
										style: ["table_main_rows", "boldHeader"],
									},
								],
								["EMPTY", "EMPTY", "EMPTY", "EMPTY"],
								[
									{
										colSpan: 3,
										text: "\nSubtotal",
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
									"",
									"",
									{
										text: "\n" + formatNumber(subtotalPrice, 2),
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
								],
								[
									{
										colSpan: 3,
										text: `VAT ${vatDisplay}%`,
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
									"",
									"",
									{
										text: formatNumber(VATPrice, 2),
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
								],
								[
									{
										colSpan: 3,
										text: "GRAND TOTAL",
										alignment: "right",
										border: [false, true, false, true],
										style: "boldHeader",
										margin: [0, 1, 0, 0],
									},
									{ text: "", border: [false, true, false, true] },
									{ text: "", border: [false, true, false, true] },
									{
										text: formatNumber(grandTotal, 2),
										alignment: "right",
										border: [false, true, false, true],
										style: "boldHeader",
										margin: [0, 1, 0, 0],
									},
								],
							],
							widths: ["55%", "15%", "15%", "15%"],
						},
						layout: {
							defaultBorder: false,
							paddingLeft: function (i, node) {
								return 0;
							},
							paddingRight: function (i, node) {
								return 0;
							},
							paddingTop: function (i, node) {
								return 0;
							},
							paddingBottom: function (i, node) {
								return 0;
							},
						},
					},
					// footer moved to page content for easy PDF merging in bulk download (1 click download)
					{
						nodeName: "DIV",
						absolutePosition: { x: 0, y: 700 },
						stack: [
							{
								stack: paymentSection,
								margin: [40, 0, 40, 20],
							},
							{
								text: [
									"This is a computer-generated Invoice. No signature is required",
								],
								style: {
									alignment: "center",
								},
							},
						],
					},
				],
			},
		],
		footer: [
			// {
			//     stack: paymentSection,
			//     margin: [40, -120, 0, 0],
			// },
			// {
			//     stack: [
			//         {
			//             text: [
			//                 "This is a computer-generated Invoice. No signature is required"
			//             ],
			//         },
			//     ],
			//     margin: [40, 20, 20, 0],
			//     style: {
			//         alignment: "center",
			//     },
			// },
		],
		styles: {
			header_lg: {
				fontSize: 27,
				margin: [0, 0, 0, 0],
			},
			sub_header: {
				decoration: "underline",
			},
			sub_header_2: {
				fontSize: 14,
				color: "#aaa",
				font: "Trirong",
				margin: [0, 0, 0, 0],
			},
			table_header: {
				margin: [0, 1, 0, 0],
				fontSize: 10,
			},
			table_main: {
				margin: [0, 5, 0, 0],
			},
			boldHeader: { bold: true },
			savings: {
				color: "green",
				bold: true,
			},
			msa_description: {
				margin: [0, 5, 90, 10],
				maxWidth: "40px",
			},
			sub_signature: {
				margin: [0, 5, 0, 0],
			},
			thai: {
				font: "Trirong",
				fontSize: 8,
				margin: [0, 0, 0, -3],
			},
			invoiceMSAStyle: {
				font: "Calibri",
				fontSize: 9,
			},
		},
		defaultStyle: {
			font: "Calibri",
			fontSize: 9,
		},
	};
	return docDefinition;
};

function InvoiceTemplate_MSA({
	copyType = "ORIGINAL",
	onDocDefinition = () => {},
	...props
}) {
	const solarCalc = useSolarCalc();
	const parseNo = solarCalc.parseFormatNumber;
	const [PDF, setPDF] = useState(null);
	const [PDFDoc, setPDFDoc] = useState(null);
	const [invoiceName, setInvoiceName] = useState(null);
	const logo = GYLogo;
	const selectedSite = props.selectedSite;
	const country = selectedSite.value.country;
	const peakLabels = usePeakLabels(country);

	const show_monthly_unitprice =
		moment(selectedSite.value?.date, "DD/MM/YYYY", true).diff(
			moment("2024-09-01", "YYYY-MM-DD"),
		) >= 0;
	const copyTypes = useMemo(
		() => ({
			ORIGINAL: "Original (ต้นฉบับ)",
			COPY: "Copy (สำเนา)",
		}),
		[],
	);

	/**
	 * Build location object
	 * @param {location string array} location
	 * @returns location object
	 */
	const buildLocationSection = (location) => {
		if (location != "")
			return [
				[
					{
						text: "Location:",
						style: ["invoice_details", { bold: true }],
					},
					{ text: location[0], style: ["invoice_details"] },
				],
				...(!!location[1]
					? [
							[
								{
									colSpan: 2,
									text: location[1],
									style: ["invoice_details"],
								},
								"",
							],
						]
					: []),
			];
		else
			return [
				[
					{
						text: "",
					},
					{ text: "" },
				],
				[
					{
						colSpan: 2,
						text: "",
					},
					"",
				],
			];
	};

	/**
	 * Build entity section object
	 * @param {Solar entity object} solarEntity
	 * @returns Entity section object
	 */
	const buildEntitySection = (solarEntity, options = { isNewInv: false }) => {
		const companyName = "Green Yellow (Thailand) Co., LTD. (Head Office)";
		const taxID = "0105557184821";
		const accountantName = !options.isNewInv
			? "Khanatpat Kamnoi (Acct.)"
			: "Accounting";
		const telephone = "+66 (0) 2-079-8081 Ext. 201";
		const address =
			"48/43-44, TISCO Tower, 19th Floor,_North Sathorn Road, Silom Sub-district,_Bangrak District, Bangkok 10500";
		const addressArr = address.split("_");
		const addressRows = addressArr.map((rows) => ({ text: rows }));
		const section = [
			{
				text: companyName,
				style: ["boldHeader"],
			},
			...addressRows,
			{
				text: `TAX ID : ${taxID}`,
			},
			{
				text: `Contact person : ${accountantName}`,
			},
			{
				text: `Tel: ${telephone}`,
			},
		];
		return section;
	};
	/**
	 * Build payment section object
	 * @param {Bank account obj} bankAccount
	 * @returns Payment section obj
	 */
	const buildPaymentSection = () => {
		const accNumber = "049-445516-8";
		const accName = "Green Yellow (Thailand) Co., LTD.";
		const bankName = "Siam Commercial Bank Public Company Limited";
		const branch = "Thanon Witthayu";
		const swiftCode = "SICOTHBK";
		const section = [
			{
				text: [
					"Please make the payment to ",
					{
						text: accName,
						style: "boldHeader",
					},
				],
			},
			{
				text: [
					"Bank : ",
					{ text: `${bankName}, `, style: "boldHeader" },
					"Branch : ",
					{ text: branch, style: "boldHeader" },
				],
			},
			{
				text: [
					"Beneficiary Account number : ",
					{ text: `${accNumber} `, style: "boldHeader" },
				],
			},
			{
				text: ["Bank Swift Code : ", { text: swiftCode, style: "boldHeader" }],
			},
		];
		return section;
	};
	/**
	 * Build extraline section object
	 * @param {Bank account obj} bankAccount
	 * @returns Extraline section obj
	 */
	const buildExtraLinesSection = (description, amount) => {
		if (description === null) return ["", "", "", ""];
		const descriptions = description.split(" / ");
		const descriptionEN = descriptions[0];
		return [
			{
				text: [
					{
						text: `${descriptionEN} \n`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			"",
			"",
			{
				text: formatNumber(amount, 2),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			},
		];
	};

	/**
	 * Generate invoice
	 * @param {Selected Site} selectedSite
	 */
	const generateInvoice = async (selectedSite) => {
		const data = selectedSite.value;
		const label = data.approvalStatus === 3 ? "Invoice" : "Proforma Invoice";
		const location = data.location.split("_");
		const billingName = data.billingName != null ? data.billingName : "-";
		const billingAddress =
			data.billingAddress != null ? data.billingAddress : "-";
		const solarEntity = data.solarEntity;
		const bankAccount = data.bankAccount;
		// Set PDF document name
		setInvoiceName(data.invNo);
		// Special discount
		let specialDiscount = data.specialDiscount; // Subtotal price
		let quantity = data.msa_day;
		let monitPrice = 0;
		let subtotalPrice = 0;
		if (quantity === 0) {
			monitPrice = 0;
			subtotalPrice = 0;
		}
		let unitCost = data.unit_price;

		if (show_monthly_unitprice) {
			unitCost = data.unit_price;
		} else {
			unitCost = data.msa_price;
		}

		monitPrice = unitCost * quantity;
		subtotalPrice = monitPrice + specialDiscount; // VAT calculations
		// MSA Period
		const selectedDate = moment(data.date, "DD/MM/YYYY", true);
		const _msaDate = moment(data.msa);
		const cod = data.cod ? moment(data.cod) : "";
		const _cod = cod ? moment(cod) : "";
		const startMonth = moment(selectedDate);
		const _startMonth = startMonth.startOf("month");
		const endMonth = moment(selectedDate);
		const _endMonth = endMonth.endOf("month");
		const _msaStartMonth = _msaDate.diff(_startMonth, "days");
		const notCODMonth = cod
			? _cod.diff(_startMonth, "months", true) > 1
			: false;
		const _period = `${_msaStartMonth < 0 ? moment(_startMonth).format("DD/MM/YYYY") : moment(_msaDate).format("DD/MM/YYYY")} - ${notCODMonth ? moment(_endMonth).format("DD/MM/YYYY") : moment(cod).subtract(1, "days").format("DD/MM/YYYY")}`;
		// VAT calculations
		const currency = data.currency;
		const vatPercent = data.vat;
		const vatDisplay = parseNo(data.vat * 100);
		const VATObj = solarCalc.calculateVAT(subtotalPrice, currency, vatPercent);
		const VATPrice = VATObj.VAT > 0 ? VATObj.VAT : 0;
		const grandTotal = subtotalPrice + VATPrice;
		// Location section
		const locationSection = buildLocationSection(location);
		// Location section
		const entitySection = buildEntitySection(solarEntity);
		// Payment section
		const paymentSection = buildPaymentSection(bankAccount);
		// PDF config
		let docDefinition;

		if (selectedDate.diff(moment("2023-08-31", "YYYY-MM-DD")) < 0) {
			// Location section
			const entitySection = buildEntitySection(solarEntity, {
				isNewInv: false,
			});
			const docDefData = {
				logo,
				entitySection,
				billingName,
				billingAddress,
				data,
				label,
				copyTypes,
				copyType,
				locationSection,
				_period,
				subtotalPrice,
				vatDisplay,
				VATPrice,
				grandTotal,
				paymentSection,
			};
			docDefinition = old_invoice_template(docDefData);
		} else {
			// below is for new invoice TH template for new envelope design since 08/31/23
			const custm_envelope = await GYAxios.get(
				`/invoice/billing_address/${data.siteID}`,
			).then((res) => res.data);
			const mail_bill_add = custm_envelope?.mail_bill_add; // site name and address combined
			const mail_name = custm_envelope?.mail_name; // name of person (from client side) who receives this invoice
			const mail_phone_no = custm_envelope?.mail_phone_no;

			// Location section
			const entitySection = buildEntitySection(solarEntity, { isNewInv: true });
			docDefinition = {
				pageSize: "A4",
				pageMargins: 0,
				content: [
					{
						nodeName: "DIV",
						style: "invoiceMSAStyle",
						stack: [
							{
								nodeName: "DIV",
								margin: [cm_to_pt(2), cm_to_pt(1.9), 0, 0],
								columns: [
									{
										nodeName: "DIV",
										width: cm_to_pt(9.5),
										stack: [
											{
												image: logo,
												width: 150,
											},
											...entitySection,
										],
									},
									{
										margin: [cm_to_pt(0.5), 0, 0, 0],
										table: {
											headerRows: 1,
											widths: [cm_to_pt(7.5)],
											heights: [cm_to_pt(3.8)],
											body: [
												[
													{
														nodeName: "DIV",
														stack: [
															{ text: label, style: "header_lg_new" },
															{
																text: copyTypes[copyType],
																style: "sub_header_2_new",
																margin: 0,
															},
															{
																nodeName: "DIV",
																style: "address_window_text",
																stack: [
																	{
																		text: mail_bill_add
																			? `Send to:\n${mail_bill_add}`
																			: `Send to: ${billingName}\n${billingAddress}`,
																		lineHeight: 0.8,
																		style: ["address_window_send_to"],
																	},
																	{
																		text: mail_name ? `Attn: ${mail_name}` : "",
																		lineHeight: 0.8,
																	},
																	{
																		text: mail_phone_no
																			? `Tel: ${mail_phone_no}`
																			: "",
																		lineHeight: 0.8,
																	},
																],
															},
														],
													},
												],
											],
										},
										layout: "noBorders",
									},
								],
							},
							{
								nodeName: "DIV",
								margin: [cm_to_pt(2), 0],
								columns: [
									{
										nodeName: "DIV",
										margin: [20, 10],
										width: "50%",
										stack: [
											{
												text: billingName,
												style: ["boldHeader"],
											},
											{
												text: billingAddress,
											},
											{
												text: `TAX ID : ${data.taxID}`,
											},
										],
									},
									{
										margin: [20, 10],
										width: "50%",
										table: {
											widths: [cm_to_pt(1.8), "*"],
											body: [
												[
													{
														text: "INVOICE NO.",
														style: ["boldHeader"],
													},
													{
														text: data.invNo,
														style: ["invoice_details"],
													},
												],
												[
													{
														text: "DATE",
														style: ["boldHeader"],
													},
													{
														text: data.invIssuedDate,
														style: ["invoice_details"],
													},
												],
												// [
												// 	{
												// 		text: "CUSTOMER ID",
												// 		style: ["boldHeader"],
												// 	},
												// 	{ text: data.customerID, style: ["invoice_details"] },
												// ],
												[
													{
														text: "Credit Term",
														style: ["boldHeader"],
													},
													{
														text: data.creditTerm + " days",
														style: ["invoice_details"],
													},
												],
												[
													{
														text: "Due date",
														style: ["boldHeader"],
													},
													{ text: data.dueDate, style: ["invoice_details"] },
												],
												locationSection[0],
												...(locationSection[1] ? [locationSection[1]] : []),
												...(data.invTopRightNote != null &&
												Object.keys(data.invTopRightNote).length > 0
													? Object.entries(data.invTopRightNote).map(
															([key, value]) => [
																{
																	text: key + ":",
																	style: ["boldHeader"],
																},
																{
																	text: value,
																	style: ["invoice_details"],
																},
															],
														)
													: []),
											],
										},
										layout: {
											defaultBorder: false,
										},
									},
								],
							},
							{
								style: "table_main",
								margin: [cm_to_pt(2), 0],
								table: {
									body: [
										[
											{
												text: "DESCRIPTION",
												border: [false, true, false, true],
												style: ["table_header", "boldHeader"],
											},
											{
												border: [false, true, false, true],
												text: "QUANTITY",
												alignment: "right",
												style: ["table_header", "boldHeader"],
											},
											{
												border: [false, true, false, true],
												text: "UNIT COST",
												alignment: "right",
												style: ["table_header", "boldHeader"],
											},
											{
												border: [false, true, false, true],
												text: "TOTAL",
												alignment: "right",
												style: ["table_header", "boldHeader"],
											},
										],
										[
											{
												text: `Period: ${_period}`,
												style: ["table_main_rows", "boldHeader"],
												colSpan: 4,
											},
										],
										[
											{
												text: `Note: \n${data.msa_description}`,
												style: ["msa_description"],
												alignment: "left",
												colSpan: 2,
											},
											"",
											"",
											"",
										],
										[
											{
												text: "Monitoring Service",
												style: "table_main_rows",
												rowSpan: 2,
											},
											{
												text: quantity,
												alignment: "right",
												rowSpan: 2,
												style: "table_main_rows",
												padding: 0,
											},
											{
												text: formatNumber(unitCost, 2),
												alignment: "right",
												rowSpan: 2,
												style: "table_main_rows",
											},
											{
												text: formatNumber(monitPrice, 2),
												alignment: "right",
												rowSpan: 2,
												style: ["table_main_rows", "boldHeader"],
											},
										],
										["", "", "", ""],
										buildExtraLinesSection(
											data.specialComment,
											data.specialDiscount,
										),
										[
											{
												colSpan: 3,
												text: "\nSubtotal",
												alignment: "right",
												style: ["table_main_rows", "boldHeader"],
											},
											"",
											"",
											{
												text: "\n" + formatNumber(subtotalPrice, 2),
												alignment: "right",
												style: ["table_main_rows", "boldHeader"],
											},
										],
										[
											{
												colSpan: 3,
												text: `VAT ${vatDisplay}%`,
												alignment: "right",
												style: ["table_main_rows", "boldHeader"],
											},
											"",
											"",
											{
												text: formatNumber(VATPrice, 2),
												alignment: "right",
												style: ["table_main_rows", "boldHeader"],
											},
										],
										[
											{
												colSpan: 3,
												text: "GRAND TOTAL",
												alignment: "right",
												border: [false, true, false, true],
												style: "boldHeader",
												margin: [0, 1, 0, 0],
											},
											{ text: "", border: [false, true, false, true] },
											{ text: "", border: [false, true, false, true] },
											{
												text: formatNumber(grandTotal, 2),
												alignment: "right",
												border: [false, true, false, true],
												style: "boldHeader",
												margin: [0, 1, 0, 0],
											},
										],
									],
									widths: ["55%", "15%", "15%", "15%"],
									// widths: [ cm_to_pt(3), '*', '*', '*' ],
									heights: 9,
								},
								layout: {
									defaultBorder: false,
									paddingLeft: function (i, node) {
										return 0;
									},
									paddingRight: function (i, node) {
										return 0;
									},
									paddingTop: function (i, node) {
										return 0;
									},
									paddingBottom: function (i, node) {
										return 0;
									},
								},
							},
							// footer moved to page content for easy PDF merging in bulk download (1 click download)
							{
								nodeName: "DIV",
								absolutePosition: { x: 0, y: cm_to_pt(26) },
								stack: [
									{
										stack: paymentSection,
										margin: [cm_to_pt(2), 0, cm_to_pt(2), 20],
									},
									{
										text: [
											"This is a computer-generated Invoice. No signature is required",
										],
										style: {
											alignment: "center",
										},
									},
								],
							},
						],
					},
				],
				footer: [
					// {
					//     stack: paymentSection,
					//     margin: [40, -120, 0, 0],
					// },
					// {
					//     stack: [
					//         {
					//             text: [
					//                 "This is a computer-generated Invoice. No signature is required"
					//             ],
					//         },
					//     ],
					//     margin: [40, 20, 20, 0],
					//     style: {
					//         alignment: "center",
					//     },
					// },
				],
				styles: {
					header_lg_new: {
						fontSize: 13,
						margin: [0, 0, 0, 0],
						alignment: "right",
					},
					sub_header: {
						decoration: "underline",
					},
					sub_header_2_new: {
						fontSize: 10,
						color: "#aaa",
						font: "Trirong",
						margin: [0, -3, 0, 0],
						alignment: "right",
					},
					address_window_text: {
						fontSize: 8,
						font: "Trirong",
						margin: 0,
					},
					address_window_send_to: {
						bold: true,
					},
					table_header: {
						margin: [0, 1, 0, 0],
						fontSize: 10,
					},
					table_main: {
						margin: [0, 5, 0, 0],
					},
					table_main_rows: {
						margin: 0,
					},
					boldHeader: { bold: true },
					savings: {
						color: "green",
						bold: true,
					},
					msa_description: {
						margin: [0, 5, 90, 10],
						maxWidth: "40px",
					},
					sub_signature: {
						margin: [0, 5, 0, 0],
					},
					thai: {
						font: "Trirong",
						fontSize: 8,
						margin: [0, 0, 0, -3],
					},
					invoiceMSAStyle: {
						font: "Calibri",
						fontSize: 9,
					},
				},
				defaultStyle: {
					font: "Calibri",
					fontSize: 9,
				},
			};
		}

		// notify parent that docDefinition is ready
		onDocDefinition(clone(docDefinition));

		// Generate PDFMAKE document
		const pdfDocGenerator = pdfMake.createPdf(docDefinition);
		// Set PDFMAKE config for download in the later stage
		setPDFDoc(docDefinition);
		if (PDF == null && selectedSite != null) {
			// Get dataURL of document for download
			pdfDocGenerator.getDataUrl((dataUrl) => {
				setPDF(dataUrl);
			});
		}
	};
	/**
	 * Download PDF document
	 * @returns PDF document
	 */
	const downloadPDF = () =>
		pdfMake.createPdf(PDFDoc).download(`${invoiceName}.pdf`);
	useMemo(() => {
		const labelsAreReady = !peakLabels.includes("");
		if (props.selectedSite != null && labelsAreReady)
			generateInvoice(props.selectedSite);
		return () => setInvoiceName(null);
	}, [props.selectedSite, peakLabels]);
	return (
		<>
			{PDF != null ? (
				<>
					<iframe style={{ width: "98%", height: "72vh" }} src={PDF} />
					<Row>
						<Button onClick={downloadPDF}>Download</Button>
					</Row>
				</>
			) : (
				<LoaderGraph style={{ height: "72vh" }} />
			)}
		</>
	);
}
export default InvoiceTemplate_MSA;
